import React, { useState, useRef } from 'react';
import { Sparkles, Upload, Download, RefreshCw, Image, Palette, Sun, Zap } from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const NeonLogoGenerator = ({ isDark, authFetch, showToast }) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [neonColor, setNeonColor] = useState('white');
  const [backgroundStyle, setBackgroundStyle] = useState('dark');
  const [glowIntensity, setGlowIntensity] = useState('medium');
  const fileInputRef = useRef(null);

  const neonColors = [
    { id: 'white', name: 'White', color: '#ffffff', glow: 'rgba(255,255,255,0.8)' },
    { id: 'cyan', name: 'Cyan', color: '#00ffff', glow: 'rgba(0,255,255,0.8)' },
    { id: 'pink', name: 'Hot Pink', color: '#ff00ff', glow: 'rgba(255,0,255,0.8)' },
    { id: 'green', name: 'Neon Green', color: '#00ff88', glow: 'rgba(0,255,136,0.8)' },
    { id: 'blue', name: 'Electric Blue', color: '#0066ff', glow: 'rgba(0,102,255,0.8)' },
    { id: 'yellow', name: 'Golden', color: '#ffff00', glow: 'rgba(255,255,0,0.8)' },
    { id: 'red', name: 'Red', color: '#ff3333', glow: 'rgba(255,51,51,0.8)' },
    { id: 'purple', name: 'Purple', color: '#9933ff', glow: 'rgba(153,51,255,0.8)' },
    { id: 'orange', name: 'Orange', color: '#ff6600', glow: 'rgba(255,102,0,0.8)' },
    { id: 'multicolor', name: 'Rainbow', color: 'linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #9400d3)', glow: 'rgba(255,255,255,0.5)' },
  ];

  const backgroundStyles = [
    { id: 'dark', name: 'Dark Wall', description: 'Classic dark background' },
    { id: 'brick', name: 'Brick Wall', description: 'Rustic brick texture' },
    { id: 'concrete', name: 'Concrete', description: 'Industrial look' },
    { id: 'wood', name: 'Wood Panel', description: 'Warm wooden background' },
  ];

  const glowIntensities = [
    { id: 'subtle', name: 'Subtle', description: 'Soft, elegant glow' },
    { id: 'medium', name: 'Medium', description: 'Balanced glow effect' },
    { id: 'intense', name: 'Intense', description: 'Bright, eye-catching' },
    { id: 'ultra', name: 'Ultra', description: 'Maximum impact' },
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a JPEG, PNG, GIF, or WEBP image', 'error');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('File is too large. Maximum size is 10MB', 'error');
      return;
    }

    setUploadedImage(file);
    setGeneratedImage(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const generateNeonLogo = async () => {
    if (!uploadedImage) {
      showToast('Please upload a logo first', 'error');
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedImage);
      formData.append('neon_color', neonColor);
      formData.append('background_style', backgroundStyle);
      formData.append('glow_intensity', glowIntensity);

      const response = await authFetch(`${API_URL}/api/admin/generate-neon-logo`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedImage({
          base64: data.image_base64,
          url: data.image_url,
          filename: data.filename
        });
        showToast('Neon logo generated successfully!');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to generate neon logo', 'error');
      }
    } catch (error) {
      console.error('Error generating neon logo:', error);
      showToast('Error generating neon logo. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage.base64}`;
    link.download = `neon_logo_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Image downloaded!');
  };

  const selectedColor = neonColors.find(c => c.id === neonColor);

  return (
    <div className="space-y-6" data-testid="neon-logo-generator-tab">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="p-3 rounded-xl relative overflow-hidden"
          style={{ 
            background: isDark 
              ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)'
              : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)',
            boxShadow: isDark ? '0 0 30px rgba(255, 0, 255, 0.5)' : '0 0 20px rgba(217, 70, 239, 0.3)'
          }}
        >
          <Sparkles className="w-6 h-6" style={{ color: '#fff' }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
            Neon Logo Generator
          </h2>
          <p style={{ color: isDark ? '#888' : '#64748b' }}>
            Transform any logo into a stunning neon sign mockup
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Upload & Settings */}
        <div className="space-y-6">
          {/* Upload Section */}
          <div 
            className="p-6 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px dashed ${isDark ? '#444' : '#e2e8f0'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <Upload className="w-5 h-5" /> Upload Your Logo
            </h3>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileUpload}
              className="hidden"
            />

            {!uploadedPreview ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-12 rounded-xl border-2 border-dashed transition-all hover:border-solid"
                style={{
                  borderColor: isDark ? '#444' : '#cbd5e1',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
                }}
              >
                <Image className="w-12 h-12 mx-auto mb-3" style={{ color: isDark ? '#666' : '#94a3b8' }} />
                <p style={{ color: isDark ? '#888' : '#64748b' }}>Click to upload your logo</p>
                <p className="text-xs mt-1" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                  JPEG, PNG, GIF, or WEBP (max 10MB)
                </p>
              </button>
            ) : (
              <div className="relative">
                <img 
                  src={uploadedPreview} 
                  alt="Uploaded logo" 
                  className="w-full max-h-64 object-contain rounded-lg mx-auto"
                  style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute top-2 right-2 p-2 rounded-lg"
                  style={{ 
                    backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
                    color: isDark ? '#fff' : '#0f172a'
                  }}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Neon Color Selection */}
          <div 
            className="p-6 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <Palette className="w-5 h-5" /> Neon Color
            </h3>
            <div className="grid grid-cols-5 gap-3">
              {neonColors.map((color) => (
                <button
                  key={color.id}
                  onClick={() => setNeonColor(color.id)}
                  className="relative p-1 rounded-xl transition-all"
                  style={{
                    border: neonColor === color.id 
                      ? `3px solid ${color.id === 'multicolor' ? '#fff' : color.color}`
                      : '3px solid transparent',
                    boxShadow: neonColor === color.id ? `0 0 15px ${color.glow}` : 'none'
                  }}
                >
                  <div 
                    className="w-full aspect-square rounded-lg"
                    style={{ 
                      background: color.color,
                      boxShadow: `0 0 10px ${color.glow}`
                    }}
                  />
                  <p className="text-xs mt-1 text-center truncate" style={{ color: isDark ? '#888' : '#64748b' }}>
                    {color.name}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Background Style */}
          <div 
            className="p-6 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <Image className="w-5 h-5" /> Background Style
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {backgroundStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setBackgroundStyle(style.id)}
                  className="p-3 rounded-lg text-left transition-all"
                  style={{
                    backgroundColor: backgroundStyle === style.id 
                      ? (isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(14, 165, 233, 0.2)')
                      : (isDark ? '#1a1a1a' : '#fff'),
                    border: `2px solid ${backgroundStyle === style.id 
                      ? (isDark ? '#00ffff' : '#0ea5e9')
                      : (isDark ? '#333' : '#e2e8f0')}`
                  }}
                >
                  <p className="font-medium" style={{ color: isDark ? '#fff' : '#0f172a' }}>{style.name}</p>
                  <p className="text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>{style.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Glow Intensity */}
          <div 
            className="p-6 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <Sun className="w-5 h-5" /> Glow Intensity
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {glowIntensities.map((intensity) => (
                <button
                  key={intensity.id}
                  onClick={() => setGlowIntensity(intensity.id)}
                  className="p-3 rounded-lg text-center transition-all"
                  style={{
                    backgroundColor: glowIntensity === intensity.id 
                      ? (isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(217, 70, 239, 0.2)')
                      : (isDark ? '#1a1a1a' : '#fff'),
                    border: `2px solid ${glowIntensity === intensity.id 
                      ? (isDark ? '#ff00ff' : '#d946ef')
                      : (isDark ? '#333' : '#e2e8f0')}`
                  }}
                >
                  <p className="font-medium text-sm" style={{ color: isDark ? '#fff' : '#0f172a' }}>{intensity.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateNeonLogo}
            disabled={!uploadedImage || isGenerating}
            className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all"
            style={{
              background: (!uploadedImage || isGenerating)
                ? (isDark ? '#333' : '#e2e8f0')
                : `linear-gradient(135deg, ${selectedColor?.color || '#ff00ff'} 0%, #00ffff 100%)`,
              color: (!uploadedImage || isGenerating) ? (isDark ? '#666' : '#94a3b8') : '#000',
              boxShadow: (!uploadedImage || isGenerating) ? 'none' : `0 0 30px ${selectedColor?.glow || 'rgba(255,0,255,0.5)'}`,
              opacity: (!uploadedImage || isGenerating) ? 0.5 : 1
            }}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin w-6 h-6 border-3 border-current border-t-transparent rounded-full" />
                Generating Neon Magic... (may take up to 60s)
              </>
            ) : (
              <>
                <Zap className="w-6 h-6" /> Generate Neon Logo
              </>
            )}
          </button>
        </div>

        {/* Right Column - Preview */}
        <div 
          className="p-6 rounded-xl min-h-[600px] flex flex-col"
          style={{
            backgroundColor: isDark ? '#0a0a0a' : '#1a1a1a',
            border: `2px solid ${isDark ? '#333' : '#333'}`,
            boxShadow: generatedImage ? `0 0 50px ${selectedColor?.glow || 'rgba(255,0,255,0.3)'}` : 'none'
          }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#ffffff' }}>
            <Sparkles className="w-5 h-5" style={{ color: selectedColor?.color || '#ff00ff' }} /> 
            Neon Preview
          </h3>

          <div className="flex-1 flex items-center justify-center rounded-xl overflow-hidden" style={{ backgroundColor: '#111' }}>
            {isGenerating ? (
              <div className="text-center">
                <div 
                  className="w-20 h-20 mx-auto mb-4 rounded-full animate-pulse"
                  style={{ 
                    background: `linear-gradient(135deg, ${selectedColor?.color || '#ff00ff'} 0%, #00ffff 100%)`,
                    boxShadow: `0 0 40px ${selectedColor?.glow || 'rgba(255,0,255,0.8)'}`
                  }}
                />
                <p style={{ color: '#888' }}>Creating your neon masterpiece...</p>
                <p className="text-xs mt-2" style={{ color: '#555' }}>This may take up to 60 seconds</p>
              </div>
            ) : generatedImage ? (
              <img 
                src={`data:image/png;base64,${generatedImage.base64}`}
                alt="Generated neon logo"
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-center p-8">
                <div 
                  className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ 
                    border: `3px dashed ${isDark ? '#333' : '#444'}`,
                  }}
                >
                  <Sparkles className="w-10 h-10" style={{ color: '#444' }} />
                </div>
                <p style={{ color: '#666' }}>Your neon logo will appear here</p>
                <p className="text-xs mt-2" style={{ color: '#444' }}>Upload a logo and click Generate</p>
              </div>
            )}
          </div>

          {/* Download Button */}
          {generatedImage && (
            <button
              onClick={downloadImage}
              className="mt-4 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
              style={{
                background: `linear-gradient(135deg, ${selectedColor?.color || '#00ff88'} 0%, #00ffff 100%)`,
                color: '#000',
                boxShadow: `0 0 20px ${selectedColor?.glow || 'rgba(0,255,136,0.5)'}`
              }}
            >
              <Download className="w-5 h-5" /> Download Neon Logo
            </button>
          )}
        </div>
      </div>

      {/* Tips Section */}
      <div 
        className="p-4 rounded-xl"
        style={{
          backgroundColor: isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)',
          border: `1px solid ${isDark ? '#ff00ff40' : '#d946ef40'}`
        }}
      >
        <h4 className="font-semibold mb-2" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
          Tips for Best Results
        </h4>
        <ul className="text-sm space-y-1" style={{ color: isDark ? '#888' : '#64748b' }}>
          <li>• Use logos with clear, simple lines and shapes for the best neon effect</li>
          <li>• High contrast logos (dark on light or light on dark) work best</li>
          <li>• Text-based logos and line art produce the most realistic neon results</li>
          <li>• For complex logos, try the "intense" or "ultra" glow settings</li>
        </ul>
      </div>
    </div>
  );
};

export default NeonLogoGenerator;
